import { Divider, Empty, Flex, Skeleton } from "antd";
import { useEffect, useRef, useState } from "react";
import { CommentComposer } from "./CommentComposer";
import type { CommentAuthor, CommentEntry, TaskSummary } from "./CommentItem";
import { CommentItem } from "./CommentItem";
import styles from "./CommentsTab.module.css";

export type { CommentAuthor, CommentEntry, TaskSummary };

type CommentsTabProps = {
  ticketId: string;
  currentUserId: number | undefined;
  taskId?: string;
  taskMap?: Record<string, TaskSummary>;
  onList: (ticketId: string, taskId?: string) => Promise<CommentEntry[]>;
  onCreate: (
    ticketId: string,
    content: string,
    userId: number,
    replyTo?: number,
    ticketItem?: string
  ) => Promise<void>;
  onUpdate: (id: string, content: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  stickyComposer?: boolean;
  messengerLayout?: boolean;
};

export const CommentsTab = ({
  ticketId,
  currentUserId,
  taskId,
  taskMap,
  onList,
  onCreate,
  onUpdate,
  onDelete,
  stickyComposer = false,
  messengerLayout = false,
}: CommentsTabProps) => {
  const composerRef = useRef<HTMLDivElement>(null);
  const [comments, setComments] = useState<CommentEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{
    id: number;
    name: string;
    ticketItem?: string | null;
  } | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(false);
    onList(ticketId, taskId)
      .then((result) => {
        setComments(result);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [ticketId, taskId, onList]);

  const refetch = () =>
    onList(ticketId, taskId)
      .then(setComments)
      .catch(() => setError(true));

  const handleSubmit = async (content: string, replyTo?: number) => {
    if (!currentUserId) return;
    const ticketItem =
      taskId ??
      (replyTo !== undefined ? (replyingTo?.ticketItem ?? undefined) : undefined);
    await onCreate(ticketId, content, currentUserId, replyTo, ticketItem);
    setReplyingTo(null);
    await refetch();
  };

  const handleEdit = async (id: string, content: string) => {
    await onUpdate(id, content);
    await refetch();
  };

  const handleDelete = async (id: string) => {
    await onDelete(id);
    await refetch();
  };

  const handleReply = (user: CommentAuthor, ticketItem?: string | null) => {
    const nameParts = [user.first_name?.trim(), user.last_name?.trim()].filter(Boolean);
    const name = nameParts.length > 0 ? nameParts.join(" ") : user.username;
    setReplyingTo({ id: user.id, name, ticketItem });
    setTimeout(() => {
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      composerRef.current?.querySelector("textarea")?.focus();
    }, 0);
  };

  if (loading) {
    return (
      <Flex vertical gap={16}>
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 2 }} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Failed to load comments" />
    );
  }

  const displayComments = messengerLayout ? [...comments].reverse() : comments;

  return (
    <Flex vertical gap={0}>
      {comments.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No comments yet" />
      ) : (
        <Flex vertical gap={0}>
          {displayComments.map((comment, index) => (
            <div key={comment.id ?? index}>
              <CommentItem
                comment={comment}
                currentUserId={currentUserId}
                onReply={handleReply}
                onEdit={handleEdit}
                onDelete={handleDelete}
                taskMap={taskMap}
                messengerLayout={messengerLayout}
              />
              {!messengerLayout && index < displayComments.length - 1 && (
                <Divider style={{ margin: "12px 0" }} />
              )}
            </div>
          ))}
        </Flex>
      )}
      <Flex
        vertical
        ref={composerRef}
        className={stickyComposer ? styles.stickyComposer : undefined}
      >
        <Divider style={{ margin: "16px 0 12px" }} />
        <CommentComposer
          onSubmit={handleSubmit}
          replyingTo={replyingTo}
          onClearReply={() => setReplyingTo(null)}
        />
      </Flex>
    </Flex>
  );
};
